import React, { useEffect, useState } from 'react';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Stack, Tooltip, Typography, TextField } from "@mui/material";
import _ from "lodash";
import Div from "@jumbo/shared/Div";
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ConfirmDialog from 'app/shared/widgets/ConfirmDialog';
import { LoadingButton } from '@mui/lab';
import { LocalizationProvider, TimePicker } from '@mui/x-date-pickers';
import {AdapterDateFns} from "@mui/x-date-pickers/AdapterDateFns";
import { esES } from '@mui/x-date-pickers/locales';
import { es } from 'date-fns/locale';
import deliveryServices from 'app/services/delivery-services';
import { inheritedAddressSchedule } from 'app/utils/appHelpers';

const DeliveryScheduleForm = props => {
    const [saving, setSaving] = useState(false)
    const [deleting, setDeleting] = useState(false)
    const [confirmDelete, setConfirmDelete] = useState(false)
    const [newStart, setNewStart] = useState(null)
    const [newEnd, setNewEnd] = useState(null)

    useEffect(() => {
        // Si la entrega no tiene horario propio, se parte del heredado de la
        // dirección del cliente para que guardar lo fije tal cual se ve.
        const own = props.delivery?.preferred_arrival_start || props.delivery?.preferred_arrival_end
        const inherited = own ? null : inheritedAddressSchedule(props.delivery)
        setNewStart(props.delivery?.preferred_arrival_start || inherited?.start || null)
        setNewEnd(props.delivery?.preferred_arrival_end || inherited?.end || null)
    }, [])

    const {delivery} = props;

    // La entrega tiene horario propio guardado (distinto del heredado de la
    // dirección del cliente), que es lo único que se puede eliminar.
    const hasOwnSchedule = Boolean(delivery?.preferred_arrival_start || delivery?.preferred_arrival_end)

    const persistSchedule = (start, end, setBusy) => {
        setBusy(true);
        deliveryServices.updateDelivery(delivery?.id, {
            delivery: {
                preferred_arrival_start: start,
                preferred_arrival_end: end
            }
        }).then((data) => {
            delivery.preferred_arrival_start = start;
            delivery.preferred_arrival_end = end;
            delivery.meets_preferred_time_window = data?.meets_preferred_time_window;
            setBusy(false);
            props.onSave(data)
        }).catch((err) => {
            setBusy(false);
        })
    }

    const saveDelivery = () => persistSchedule(newStart, newEnd, setSaving)

    // Deja la entrega sin horario propio. Si su dirección tiene uno configurado
    // en Clientes, la tarjeta del viaje vuelve a mostrar ese heredado.
    const deleteSchedule = () => persistSchedule(null, null, setDeleting)

    const handleScheduleChange = (field, newVal) => {
        // setCanSave(true)
        if(newVal) {
            newVal = `${_.padStart(newVal.getHours(), 2, '0')}:${_.padStart(newVal.getMinutes(), 2, '0')}`
        }
        if(field === 'preferred_arrival_start') {
            setNewStart(newVal)
        } else {
            setNewEnd(newVal)
        }
    }

    const timeStringToDate = (timeStr) => {
        // Time string in format "HH:MM"
        if(timeStr) {
            var d = new Date()
            d.setHours(timeStr.substr(0,timeStr.indexOf(":")));
            d.setMinutes(timeStr.substr(timeStr.indexOf(":")+1));
            d.setSeconds(0);
            return d;
        } else {
            return null;
        }
    }

    return (
        <React.Fragment>
            <Dialog
                fullWidth={false}
                maxWidth={"sm"}
                open={props.isOpen}
                onClose={() => {
                    if(saving || deleting){ return; }
                    props.onClose()
                }}>
                <DialogTitle>
                    <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
                        <Typography variant={"h2"} color="primary">Fijar horario recomendado de entrega</Typography>
                        <Tooltip title="El conductor verá este horario en la aplicación. Configúralo solo si es obligatorio.">
                            <InfoOutlinedIcon color="action" sx={{ cursor: 'help', flexShrink: 0 }} />
                        </Tooltip>
                    </Stack>
                </DialogTitle>
                <DialogContent>
                    <Div sx={{ maxWidth: '100%', pt: 3 }}>
                        <Div sx={{display: 'flex', alignItems: 'start', justifyContent: 'space-between', mb: 2}}>
                            <LocalizationProvider
                                size="small"
                                dateAdapter={AdapterDateFns}
                                adapterLocale={es}
                                localeText={{
                                    ...esES.components.MuiLocalizationProvider.defaultProps.localeText,
                                    okButtonLabel: 'Aceptar',
                                }}
                            >
                                <TimePicker
                                    label="Desde"
                                    ampm={false}
                                    slotProps={{ actionBar: { actions: ['clear', 'accept'] } }}
                                    value={timeStringToDate(newStart)}
                                    onChange={(newValue) => { handleScheduleChange('preferred_arrival_start', newValue) }}
                                    renderInput={(params) => <TextField {...params} />}
                                />
                                <TimePicker
                                    label="Hasta"
                                    ampm={false}
                                    slotProps={{ actionBar: { actions: ['clear', 'accept'] } }}
                                    value={timeStringToDate(newEnd)}
                                    onChange={(newValue) => { handleScheduleChange('preferred_arrival_end', newValue) }}
                                    renderInput={(params) => <TextField {...params} />}
                                />
                            </LocalizationProvider>
                        </Div>
                    </Div>
                </DialogContent>
                <DialogActions>
                    { hasOwnSchedule &&
                        <Tooltip title="Deja esta entrega sin horario propio. Si su dirección tiene uno configurado en Clientes, se vuelve a mostrar ese.">
                            <span style={{ marginRight: 'auto' }}>
                                <LoadingButton
                                    loading={deleting}
                                    disabled={saving}
                                    color='error'
                                    size='small'
                                    startIcon={<DeleteOutlineOutlinedIcon/>}
                                    loadingPosition="start"
                                    onClick={() => setConfirmDelete(true)}>
                                        { deleting ? 'Eliminando' : 'Eliminar horario' }
                                </LoadingButton>
                            </span>
                        </Tooltip>
                    }
                    <Button onClick={() => {
                        if(saving || deleting){ return; }
                        props.onClose()
                    }}>Cerrar</Button>
                    <LoadingButton
                        loading={saving}
                        disabled={deleting}
                        variant="contained"
                        color='primary'
                        size='small'
                        endIcon={<SaveOutlinedIcon/>}
                        loadingPosition="end"
                        onClick={() => saveDelivery()}>
                            { saving ? 'Guardando' : 'Guardar' }
                    </LoadingButton>
                </DialogActions>

                <ConfirmDialog
                    open={confirmDelete}
                    title="Eliminar horario de la entrega"
                    message={
                        <>
                            ¿Estás seguro que deseas eliminar el horario recomendado de la entrega <strong>{delivery?.code}</strong>?
                            <br />
                            { inheritedAddressSchedule(delivery)
                                ? 'Volverá a mostrarse el horario configurado en la dirección del cliente.'
                                : 'El conductor dejará de ver un horario para esta entrega.' }
                        </>
                    }
                    onClose={() => setConfirmDelete(false)}
                    onConfirm={() => { setConfirmDelete(false); deleteSchedule(); }}
                />
            </Dialog>
        </React.Fragment>
    )
}

export default DeliveryScheduleForm;